import React from 'react';
import {
  Text, OrderedList, UnorderedList, ListItem, Table, Thead, Tbody, Tr, Th, Td,
  Alert, AlertIcon, Badge, HStack, VStack, Card, CardBody, Box, Divider, Code,
} from '@chakra-ui/react';

const StatusBadge = ({ color, children }) => (
  <Badge colorScheme={color} fontSize="xs" px={2} py={0.5} borderRadius="md">{children}</Badge>
);

const suporteManual = [
  {
    title: '1. Introdução — Papel do Suporte',
    content: (
      <>
        <Text mb={3}>
          O time de <strong>Suporte</strong> é responsável pela gestão de usuários e pela resolução de problemas reportados por contratantes e profissionais no aplicativo TOVI.
        </Text>
        <Text mb={3}>Suas responsabilidades incluem:</Text>
        <UnorderedList ml={6} spacing={1} mb={3}>
          <ListItem>Gerenciar a lista de <strong>Contratados</strong> (profissionais) — verificar contas, gerenciar fotos e certificações</ListItem>
          <ListItem>Gerenciar a lista de <strong>Contratantes</strong> (clientes) — verificar contas, visualizar perfis</ListItem>
          <ListItem>Resolver <strong>cancelamentos</strong> de conversas (iniciados por contratante ou profissional)</ListItem>
          <ListItem>Atender <strong>solicitações de ajuda</strong> de ambos os lados</ListItem>
          <ListItem>Analisar <strong>recusas de serviço</strong> e tomar ações necessárias</ListItem>
          <ListItem>Comunicar-se com usuários via <strong>Chat Administrativo</strong></ListItem>
        </UnorderedList>
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          Os badges (contadores) no menu lateral atualizam automaticamente a cada 5 minutos, mostrando quantos tickets estão pendentes em cada seção.
        </Alert>
      </>
    ),
    keywords: 'introdução suporte papel responsabilidades contratados contratantes',
  },
  {
    title: '2. Lista de Contratados (Profissionais)',
    content: (
      <>
        <Text mb={3}>
          Acesse via menu lateral: <strong>Contratados → Lista de Contratados</strong>. Esta página mostra todos os profissionais cadastrados no aplicativo.
        </Text>

        <Text fontWeight="semibold" mb={2}>Filtros disponíveis:</Text>
        <Table size="sm" mb={4} variant="simple">
          <Thead><Tr><Th>Filtro</Th><Th>Descrição</Th></Tr></Thead>
          <Tbody>
            <Tr><Td><StatusBadge color="green">Verificados</StatusBadge></Td><Td>Profissionais com conta verificada pela equipe</Td></Tr>
            <Tr><Td><StatusBadge color="red">Não Verificados</StatusBadge></Td><Td>Profissionais aguardando verificação</Td></Tr>
            <Tr><Td><StatusBadge color="yellow">Premium</StatusBadge></Td><Td>Profissionais com assinatura premium ativa</Td></Tr>
          </Tbody>
        </Table>
        <Text mb={2}>A barra de busca filtra por <strong>nome, email, profissão e especialidades</strong>.</Text>

        <Divider my={3} />
        <Text fontWeight="semibold" mb={2}>Ações por profissional:</Text>
        <Table size="sm" mb={4} variant="simple">
          <Thead><Tr><Th>Ação</Th><Th>Ícone</Th><Th>O que faz</Th></Tr></Thead>
          <Tbody>
            <Tr><Td>Detalhes</Td><Td>Olho</Td><Td>Abre modal com informações completas, fotos e certificações</Td></Tr>
            <Tr><Td>Verificar</Td><Td>Check com pessoa</Td><Td>Marca a conta como verificada (fica desabilitado se já verificada)</Td></Tr>
            <Tr><Td>Redefinir Senha</Td><Td>Cadeado</Td><Td>Abre modal para redefinir a senha do profissional</Td></Tr>
            <Tr><Td>Excluir</Td><Td>Lixeira</Td><Td>Remove o profissional (pede confirmação antes)</Td></Tr>
          </Tbody>
        </Table>

        <Divider my={3} />
        <Text fontWeight="semibold" mb={2}>Modal de Detalhes:</Text>
        <Text mb={2}>Ao clicar em "Detalhes", o modal exibe:</Text>
        <UnorderedList ml={6} spacing={1} mb={3}>
          <ListItem><strong>Informações pessoais:</strong> email, telefone, profissão, endereço, histórico de trabalho</ListItem>
          <ListItem><strong>Status da conta:</strong> badges de verificação e premium</ListItem>
          <ListItem><strong>Especialidades e categorias:</strong> tags coloridas</ListItem>
          <ListItem><strong>Fotos:</strong> galeria em grade com opção de upload de novas fotos</ListItem>
          <ListItem><strong>Certificações:</strong> lista com botão de visualização e opção de upload (aceita PDF, DOCX, JPG, PNG)</ListItem>
        </UnorderedList>
      </>
    ),
    keywords: 'contratados profissionais lista verificar premium fotos certificações detalhes excluir senha',
  },
  {
    title: '3. Lista de Contratantes (Clientes)',
    content: (
      <>
        <Text mb={3}>
          Acesse via menu lateral: <strong>Contratantes → Lista</strong>. Esta página mostra todos os clientes (contratantes) cadastrados.
        </Text>
        <Text mb={2}>A barra de busca filtra por <strong>nome e email</strong>.</Text>

        <Text fontWeight="semibold" mt={3} mb={2}>Ações por contratante:</Text>
        <Table size="sm" mb={4} variant="simple">
          <Thead><Tr><Th>Ação</Th><Th>O que faz</Th></Tr></Thead>
          <Tbody>
            <Tr><Td>Detalhes</Td><Td>Abre modal com informações, endereço, fotos de perfil e galeria</Td></Tr>
            <Tr><Td>Verificar</Td><Td>Marca a conta como verificada</Td></Tr>
            <Tr><Td>Redefinir Senha</Td><Td>Abre modal de reset de senha</Td></Tr>
            <Tr><Td>Excluir</Td><Td>Remove o contratante (pede confirmação)</Td></Tr>
          </Tbody>
        </Table>

        <Text fontWeight="semibold" mb={2}>Modal de Detalhes:</Text>
        <UnorderedList ml={6} spacing={1}>
          <ListItem><strong>Informações pessoais:</strong> nome, CPF/CNPJ, email, telefone, profissão</ListItem>
          <ListItem><strong>Status da conta:</strong> verificação e premium</ListItem>
          <ListItem><strong>Endereço completo:</strong> rua, número, complemento, bairro, cidade/UF, CEP</ListItem>
          <ListItem><strong>Imagens:</strong> foto de perfil, foto de capa, galeria de fotos</ListItem>
        </UnorderedList>
      </>
    ),
    keywords: 'contratantes clientes lista verificar detalhes endereço fotos perfil',
  },
  {
    title: '4. Cancelamentos de Contratantes',
    content: (
      <>
        <Text mb={3}>
          Acesse via: <strong>Contratantes → Cancelamentos</strong>. Mostra todas as solicitações de cancelamento de conversa feitas por contratantes.
        </Text>

        <Text fontWeight="semibold" mb={2}>Sistema de Status (workflow):</Text>
        <VStack align="stretch" spacing={2} mb={4}>
          <Card variant="outline" size="sm"><CardBody py={2} px={3}>
            <HStack><StatusBadge color="red">Aguardando</StatusBadge></HStack>
            <Text fontSize="sm" mt={1}>Estado inicial — o cancelamento acabou de chegar e ninguém olhou ainda.</Text>
          </CardBody></Card>
          <Card variant="outline" size="sm"><CardBody py={2} px={3}>
            <HStack><StatusBadge color="yellow">Em Andamento</StatusBadge></HStack>
            <Text fontSize="sm" mt={1}>Muda <strong>automaticamente</strong> quando você clica em "Detalhes". Significa que alguém está analisando.</Text>
          </CardBody></Card>
          <Card variant="outline" size="sm"><CardBody py={2} px={3}>
            <HStack><StatusBadge color="orange">Aguardando Confirmação</StatusBadge></HStack>
            <Text fontSize="sm" mt={1}>Você clicou "Finalizar" e o sistema enviou uma confirmação para o contratante. Aguardando resposta.</Text>
          </CardBody></Card>
          <Card variant="outline" size="sm"><CardBody py={2} px={3}>
            <HStack><StatusBadge color="green">Concluído</StatusBadge></HStack>
            <Text fontSize="sm" mt={1}>O cancelamento foi resolvido e confirmado.</Text>
          </CardBody></Card>
        </VStack>

        <Text fontWeight="semibold" mb={2}>Ações disponíveis:</Text>
        <UnorderedList ml={6} spacing={1} mb={3}>
          <ListItem><strong>Detalhes</strong> (ícone de olho) — Abre o modal com informações completas. Se o status for "Aguardando", muda automaticamente para "Em Andamento".</ListItem>
          <ListItem><strong>Chat</strong> (ícone de mensagem) — Abre o chat administrativo com o contratante para esclarecer dúvidas.</ListItem>
        </UnorderedList>

        <Text fontWeight="semibold" mb={2}>Filtros:</Text>
        <Text mb={2}>Use os botões de status no topo para filtrar. Cada botão mostra a contagem de tickets naquele status. O botão "Limpar Filtros" volta para a visão padrão (tickets ativos).</Text>

        <Alert status="warning" borderRadius="md" fontSize="sm">
          <AlertIcon />
          <strong>Atenção:</strong> Abrir os detalhes de um ticket "Aguardando" muda automaticamente o status para "Em Andamento". Isso é intencional — indica que alguém está trabalhando nele.
        </Alert>
      </>
    ),
    keywords: 'cancelamentos contratantes status aguardando andamento confirmação concluído finalizar workflow',
  },
  {
    title: '5. Cancelamentos de Contratados',
    content: (
      <>
        <Text mb={3}>
          Acesse via: <strong>Contratados → Cancelamentos</strong>. Funciona de forma idêntica aos cancelamentos de contratantes, mas mostra cancelamentos iniciados pelos profissionais.
        </Text>
        <Text mb={3}>O <strong>sistema de status</strong>, <strong>ações</strong> e <strong>filtros</strong> são os mesmos descritos na seção anterior. A única diferença é que o botão de Chat abre a conversa com o profissional (não com o contratante).</Text>
        <Alert status="info" borderRadius="md" fontSize="sm">
          <AlertIcon />
          Os badges no menu lateral contam separadamente os cancelamentos de contratantes e de contratados.
        </Alert>
      </>
    ),
    keywords: 'cancelamentos contratados profissionais workflow',
  },
  {
    title: '6. Solicitações de Ajuda — Contratantes',
    content: (
      <>
        <Text mb={3}>
          Acesse via: <strong>Contratantes → Preciso de Ajuda</strong>. Mostra todas as solicitações de ajuda feitas por contratantes.
        </Text>
        <Text mb={2}>A tabela exibe:</Text>
        <UnorderedList ml={6} spacing={1} mb={3}>
          <ListItem><strong>ID</strong> — identificador do ticket</ListItem>
          <ListItem><strong>Usuário</strong> — nome do contratante</ListItem>
          <ListItem><strong>Razão</strong> — motivo da solicitação</ListItem>
          <ListItem><strong>Notas Adicionais</strong> — informações extras fornecidas pelo contratante (ou "N/A")</ListItem>
          <ListItem><strong>Status</strong> — mesmo sistema de 4 estados (Aguardando → Em Andamento → Ag. Confirmação → Concluído)</ListItem>
        </UnorderedList>
        <Text mb={3}>As ações são as mesmas: <strong>Detalhes</strong> (abre modal + muda status se "Aguardando") e <strong>Chat</strong> (abre conversa com o contratante).</Text>
        <Text>Use a barra de busca para filtrar por <strong>nome, razão ou notas adicionais</strong>.</Text>
      </>
    ),
    keywords: 'ajuda contratantes preciso ajuda solicitação ticket razão notas',
  },
  {
    title: '7. Solicitações de Ajuda — Contratados',
    content: (
      <>
        <Text mb={3}>
          Acesse via: <strong>Contratados → Preciso de Ajuda</strong>. Funciona de forma idêntica às solicitações de contratantes, mas para profissionais.
        </Text>
        <Text>O mesmo sistema de status, ações e filtros se aplica. O Chat abre com o profissional.</Text>
      </>
    ),
    keywords: 'ajuda contratados profissionais preciso ajuda',
  },
  {
    title: '8. Recusa de Serviços',
    content: (
      <>
        <Text mb={3}>
          Acesse via: <strong>Contratantes → Recusa de Serviços</strong>. Mostra todas as rejeições de serviço que precisam de análise.
        </Text>
        <Text mb={2}>A tabela exibe:</Text>
        <UnorderedList ml={6} spacing={1} mb={3}>
          <ListItem><strong>ID</strong> — identificador do ticket</ListItem>
          <ListItem><strong>Serviço</strong> — descrição do serviço rejeitado</ListItem>
          <ListItem><strong>Usuário</strong> — quem solicitou a rejeição</ListItem>
          <ListItem><strong>Razão</strong> — motivo da rejeição</ListItem>
          <ListItem><strong>Data</strong> — quando foi registrada</ListItem>
          <ListItem><strong>Status</strong> — mesmo sistema de 4 estados</ListItem>
        </UnorderedList>
        <Text mb={3}>Ações: <strong>Detalhes</strong> e <strong>Chat</strong>. O mesmo workflow de status se aplica.</Text>
        <Text>A barra de busca filtra por <strong>ID, descrição do serviço, nome do usuário ou razão</strong>.</Text>
      </>
    ),
    keywords: 'recusa serviços rejeição análise serviço rejeitado',
  },
  {
    title: '9. Chat Administrativo',
    content: (
      <>
        <Text mb={3}>
          O Chat Administrativo permite comunicação direta entre a equipe de suporte e usuários/profissionais. É acessado clicando no ícone de <strong>mensagem</strong> em qualquer ticket de cancelamento, ajuda ou recusa.
        </Text>
        <Text fontWeight="semibold" mb={2}>Como funciona:</Text>
        <UnorderedList ml={6} spacing={1} mb={3}>
          <ListItem>As mensagens do <strong>Suporte</strong> aparecem em bolhas <strong>azuis</strong> à direita</ListItem>
          <ListItem>As mensagens do <strong>usuário/profissional</strong> aparecem em bolhas <strong>verdes</strong> à esquerda</ListItem>
          <ListItem>O horário é mostrado abaixo de cada mensagem (HH:MM se foi hoje, ou data completa)</ListItem>
          <ListItem>O chat rola automaticamente para a mensagem mais recente</ListItem>
        </UnorderedList>

        <Text fontWeight="semibold" mb={2}>Enviando mensagens:</Text>
        <OrderedList ml={6} spacing={1} mb={3}>
          <ListItem>Digite sua mensagem no campo de texto na parte inferior</ListItem>
          <ListItem>Clique no botão <strong>Enviar</strong> (ícone azul) ou pressione <strong>Enter</strong></ListItem>
          <ListItem>A mensagem será enviada imediatamente ao usuário</ListItem>
        </OrderedList>

        <Alert status="warning" borderRadius="md" fontSize="sm" mb={3}>
          <AlertIcon />
          O chat possui <strong>filtro de palavras inadequadas</strong>. Se uma mensagem contiver conteúdo inadequado, ela será bloqueada e um aviso será exibido. Revise o texto e tente novamente.
        </Alert>

        <Alert status="info" borderRadius="md" fontSize="sm">
          <AlertIcon />
          Se a conversa estiver vazia, a mensagem "Nenhuma mensagem encontrada. Inicie uma conversa!" será exibida. Basta digitar para iniciar o diálogo.
        </Alert>
      </>
    ),
    keywords: 'chat administrativo mensagem conversa enviar comunicação filtro',
  },
  {
    title: '10. Fluxos de Trabalho Comuns',
    content: (
      <>
        <Text fontWeight="semibold" mb={2}>Resolver um cancelamento:</Text>
        <OrderedList ml={6} spacing={1} mb={4}>
          <ListItem>Acesse <strong>Contratantes → Cancelamentos</strong> (ou Contratados)</ListItem>
          <ListItem>Veja o badge <StatusBadge color="red">Aguardando</StatusBadge> — indica tickets novos</ListItem>
          <ListItem>Clique em <strong>Detalhes</strong> no ticket → status muda para <StatusBadge color="yellow">Em Andamento</StatusBadge></ListItem>
          <ListItem>Analise a razão do cancelamento no modal</ListItem>
          <ListItem>Se precisar de mais informações, clique em <strong>Chat</strong> para falar com a pessoa</ListItem>
          <ListItem>Quando resolvido, clique em <strong>Finalizar</strong></ListItem>
          <ListItem>O sistema pode pedir confirmação do usuário → <StatusBadge color="orange">Aguardando Confirmação</StatusBadge></ListItem>
          <ListItem>Quando confirmado → <StatusBadge color="green">Concluído</StatusBadge></ListItem>
        </OrderedList>

        <Divider my={3} />

        <Text fontWeight="semibold" mb={2}>Verificar um profissional:</Text>
        <OrderedList ml={6} spacing={1} mb={4}>
          <ListItem>Acesse <strong>Contratados → Lista de Contratados</strong></ListItem>
          <ListItem>Use o filtro <StatusBadge color="red">Não Verificados</StatusBadge> para ver os pendentes</ListItem>
          <ListItem>Clique em <strong>Detalhes</strong> para revisar informações, fotos e certificações</ListItem>
          <ListItem>Se tudo estiver em ordem, feche o modal e clique no ícone de <strong>Verificar</strong> (check)</ListItem>
          <ListItem>O badge do profissional mudará para <StatusBadge color="green">Verificado</StatusBadge></ListItem>
        </OrderedList>

        <Divider my={3} />

        <Text fontWeight="semibold" mb={2}>Redefinir senha de um usuário:</Text>
        <OrderedList ml={6} spacing={1}>
          <ListItem>Na lista de contratados ou contratantes, clique no ícone de <strong>Cadeado</strong></ListItem>
          <ListItem>O modal de redefinição de senha abrirá</ListItem>
          <ListItem>Preencha a nova senha e confirme</ListItem>
          <ListItem>O usuário receberá a nova senha por email</ListItem>
        </OrderedList>
      </>
    ),
    keywords: 'fluxo trabalho workflow resolver cancelamento verificar profissional redefinir senha passo a passo',
  },
  {
    title: '11. Dicas e Observações',
    content: (
      <Table size="sm" variant="simple">
        <Thead><Tr><Th>Dica</Th><Th>Descrição</Th></Tr></Thead>
        <Tbody>
          <Tr><Td fontWeight="medium">Badges do menu</Td><Td>Os contadores no menu lateral atualizam a cada 5 minutos. Mostram tickets ativos (não concluídos).</Td></Tr>
          <Tr><Td fontWeight="medium">Auto-transição</Td><Td>Abrir "Detalhes" de um ticket "Aguardando" muda automaticamente para "Em Andamento". Isso sinaliza à equipe que alguém já está olhando.</Td></Tr>
          <Tr><Td fontWeight="medium">Busca em tempo real</Td><Td>Todas as barras de busca filtram instantaneamente, sem precisar clicar em um botão.</Td></Tr>
          <Tr><Td fontWeight="medium">Chat seguro</Td><Td>O chat tem filtro de palavras inadequadas que bloqueia o envio. Se sua mensagem for bloqueada, revise o texto.</Td></Tr>
          <Tr><Td fontWeight="medium">Fotos e certificações</Td><Td>Na lista de contratados, você pode fazer upload de fotos e certificações diretamente pelo modal de detalhes.</Td></Tr>
          <Tr><Td fontWeight="medium">Exclusões são permanentes</Td><Td>Excluir um contratante ou profissional <strong>não pode ser desfeito</strong>. Sempre confirme antes.</Td></Tr>
        </Tbody>
      </Table>
    ),
    keywords: 'dicas observações badges menu auto-transição busca chat seguro exclusão permanente',
  },
];

export default suporteManual;
